// @ts-nocheck
// @ts-ignore
import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent, ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {Object} ProductVariant
 * @property {string|number} [id] - Variant ID
 * @property {string} [title] - Variant title
 * @property {string} [name] - Variant name
 * @property {boolean} [available] - Whether variant is available
 * @property {Object} [featured_media] - Featured media object
 * @property {Object} [featured_media.preview_image] - Preview image data
 * @property {string} [featured_media.preview_image.src] - Image source URL
 * @property {string} [featured_media.alt] - Alt text for the image
 */

/**
 * @typedef {HTMLElement & {
 *   source: Element,
 *   destination: Element,
 *   useSourceSize: string | boolean
 * }} FlyToCart
 */

/**
 * @typedef {Object} StickyAddToCartRefs
 * @property {HTMLElement} stickyBar - The floating bar container
 * @property {HTMLButtonElement} addToCartButton - Sticky bar's button
 * @property {HTMLElement} quantityDisplay - Quantity display container
 * @property {HTMLElement} quantityNumber - Quantity number element
 * @property {HTMLImageElement} productImage - Product image element
 */

/**
 * A custom element that manages a sticky add-to-cart bar.
 * Shows when the main buy buttons scroll out of view.
 *
 * @extends {Component<StickyAddToCartRefs>}
 */
class StickyAddToCartComponent extends Component {
  requiredRefs = ['stickyBar', 'quantityDisplay', 'quantityNumber'];

  /** @type {IntersectionObserver | null} */
  #buyButtonsIntersectionObserver = null;

  /** @type {IntersectionObserver | null} */
  #mainBottomObserver = null;

  /** @type {number | undefined} */
  #resetTimeout;

  /** @type {boolean} */
  #isStuck = false;

  /** @type {number | null} */
  #animationTimeout = null;

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {HTMLElement | null} */
  #mainQuantitySelector = null;

  /** @type {HTMLElement | null} */
  #stickyQuantitySelector = null;

  /** @type {{ minus?: (event: Event) => void, plus?: (event: Event) => void, inputBlur?: (event: Event) => void }} */
  #quantityProxyHandlers = {};

  /** @type {HTMLButtonElement | null} */
  #targetAddToCartButton = null;

  /** @type {number} */
  #currentQuantity = 1;

  /** @type {boolean} */
  #hiddenByBottom = false;

  connectedCallback() {
    super.connectedCallback();

    this.#setupIntersectionObserver();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section');

    // Listen on both section and document to catch events
    if (target) {
      target.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
      target.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected, { signal });
    }

    // Also listen on document as fallback
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.cartError, this.#handleCartAddComplete, { signal });
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#handleQuantityUpdate, { signal });

    this.#getInitialQuantity();
    this.#setupQuantitySync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#buyButtonsIntersectionObserver?.disconnect();
    this.#mainBottomObserver?.disconnect();
    this.#abortController.abort();
    if (this.#animationTimeout) {
      clearTimeout(this.#animationTimeout);
    }

    // Tear down quantity proxy listeners
    if (this.#stickyQuantitySelector && this.#quantityProxyHandlers) {
      const minusButton = this.#stickyQuantitySelector.querySelector('[ref="minusButton"]');
      const plusButton = this.#stickyQuantitySelector.querySelector('[ref="plusButton"]');
      const quantityInput = this.#stickyQuantitySelector.querySelector('input[ref="quantityInput"]');

      if (minusButton && this.#quantityProxyHandlers.minus) {
        minusButton.removeEventListener('click', this.#quantityProxyHandlers.minus, true);
      }
      if (plusButton && this.#quantityProxyHandlers.plus) {
        plusButton.removeEventListener('click', this.#quantityProxyHandlers.plus, true);
      }
      if (quantityInput && this.#quantityProxyHandlers.inputBlur) {
        quantityInput.removeEventListener('blur', this.#quantityProxyHandlers.inputBlur, true);
      }
    }
  }

  /**
   * Sets up the IntersectionObserver to watch the buy buttons visibility
   */
  #setupIntersectionObserver() {
    const productForm = this.#getProductForm();
    if (!productForm) return;

    const buyButtonsBlock = productForm.closest('.buy-buttons-block');
    if (!buyButtonsBlock) return;

    // In themes migrated from 2.0, the footer element doesn't exist
    const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
    if (!footer) return;

    // Observer for buy buttons visibility
    this.#buyButtonsIntersectionObserver = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (!entry) return;

      // Only show sticky bar if buy buttons have been scrolled past (above viewport)
      if (!entry.isIntersecting && !this.#isStuck) {
        // Check if the element is above the viewport (scrolled past) or below (not yet reached)
        const rect = entry.target.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top < 0) {
          // Element is above viewport - show sticky bar
          this.#showStickyBar();
        }
        // If rect.top >= 0, element is below viewport - don't show sticky bar yet
      } else if (entry.isIntersecting && this.#isStuck) {
        this.#hiddenByBottom = false;
        this.#hideStickyBar();
      }
    });

    // Observer for footer visibility - hides sticky bar at page bottom
    this.#mainBottomObserver = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (entry.isIntersecting && this.#isStuck) {
          this.#hiddenByBottom = true;
          this.#hideStickyBar();
        } else if (!entry.isIntersecting && this.#hiddenByBottom) {
          // Footer out of view - check if we should show sticky bar again
          const rect = buyButtonsBlock.getBoundingClientRect();
          // Only show if buy buttons are above the viewport (scrolled past)
          if (rect.bottom < 0 || rect.top < 0) {
            this.#hiddenByBottom = false;
            this.#showStickyBar();
          }
        }
      },
      {
        rootMargin: '200px 0px 0px 0px',
      }
    );

    this.#buyButtonsIntersectionObserver.observe(buyButtonsBlock);
    this.#mainBottomObserver.observe(footer);
    this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');

    // Cache the main quantity selector for quantity sync
    this.#mainQuantitySelector = productForm.querySelector('quantity-selector-component');
  }

  // Public action handlers
  /**
   * Sets up syncing between the sticky quantity selector and the main product quantity selector.
   * Sticky controls proxy their actions to the main selector so the actual form quantity is used.
   */
  #setupQuantitySync() {
    // Quantity syncing between sticky and main selectors is handled by custom-product-atc.js
    // The proxy mechanism was intercepting clicks with stopImmediatePropagation() which
    // prevented the sticky quantity-selector-component's own handlers from working.
    // Now the sticky selector works independently and custom-product-atc.js syncs both inputs.
    const productForm = this.#getProductForm();
    if (!productForm) return;

    this.#mainQuantitySelector = productForm.querySelector('quantity-selector-component');
    this.#stickyQuantitySelector = this.querySelector('.sticky-add-to-cart__quantity-selector-wrapper quantity-selector-component');
  }

  /**
   * Handles the add to cart button click in the sticky bar
   */
  handleAddToCartClick = async () => {
    const stickyAddToCartButton = /** @type {HTMLButtonElement | null} */ (
      this.querySelector('[ref="addToCartButton"]')
    );

    if (!this.#targetAddToCartButton) return;
    if (!stickyAddToCartButton) return;

    // Don't animate if button is disabled
    if (stickyAddToCartButton.disabled || this.#targetAddToCartButton.disabled) {
      return;
    }

    // Check if variant is selected - same validation as main ATC button
    const productForm = this.closest('.shopify-section')?.querySelector('product-form-component');
    if (productForm) {
      /** @type {HTMLInputElement | null} */
      const variantIdInput = productForm.querySelector('input[name="id"][ref="variantId"]');
      const section = productForm.closest('.shopify-section');
      if (!section) return;

      const hasVariantPicker = section.querySelector('variant-picker');

      // If there's a variant picker but no variant ID, scroll to picker and show error
      if (hasVariantPicker && (!variantIdInput || !variantIdInput.value)) {
        /** @type {HTMLElement | null} */
        const variantPicker = section.querySelector(
          'variant-picker:not(product-card variant-picker):not(quick-add-dialog variant-picker)'
        );

        if (variantPicker) {
          // Scroll to variant picker
          variantPicker.scrollIntoView({ behavior: 'smooth', block: 'center' });

          // Show error message
          /** @type {HTMLElement | null} */
          const variantError = variantPicker.querySelector('[data-variant-error]');
          if (variantError) {
            variantError.textContent = 'Please Select a Size';
            variantError.classList.remove('hidden');

            // Highlight the variant picker
            variantPicker.style.outline = '2px solid #EE3224';
            variantPicker.style.outlineOffset = '4px';
            setTimeout(() => {
              variantPicker.style.outline = '';
              variantPicker.style.outlineOffset = '';
            }, 2000);

            // Hide error after 5 seconds
            setTimeout(() => {
              if (
                variantError &&
                (!variantIdInput || (variantIdInput instanceof HTMLInputElement && !variantIdInput.value))
              ) {
                variantError.classList.add('hidden');
              }
            }, 5000);
          }
        }

        return;
      }

    }

    this.#targetAddToCartButton.dataset.puppet = 'true';
    this.#targetAddToCartButton.click();
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    if (stickyAddToCartButton.dataset.added !== 'true') {
      stickyAddToCartButton.dataset.added = 'true';
    }

    if (!cartIcon || !this.refs.productImage) return;
    if (this.#resetTimeout) clearTimeout(this.#resetTimeout);

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));
    const sourceStyles = getComputedStyle(this.refs.productImage);

    flyToCartElement.classList.add('fly-to-cart--sticky');
    flyToCartElement.style.setProperty('background-image', `url(${this.refs.productImage.src})`);
    flyToCartElement.useSourceSize = 'true';
    flyToCartElement.source = this.refs.productImage;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);

    await onAnimationEnd([stickyAddToCartButton, flyToCartElement]);
    this.#resetTimeout = setTimeout(() => {
      stickyAddToCartButton.removeAttribute('data-added');
    }, 800);
  };

  /**
   * Handles variant update events
   * @param {CustomEvent} event - The variant update event
   */
  #handleVariantUpdate = (event) => {
    if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const variant = event.detail.resource;

    // Get the new sticky add to cart HTML from the server response
    const newStickyAddToCart = event.detail.data.html.querySelector('sticky-add-to-cart');
    if (!newStickyAddToCart) {
      return;
    }

    const newStickyBar = newStickyAddToCart.querySelector('[ref="stickyBar"]');
    if (!newStickyBar) {
      console.warn('[StickyAddToCart] No newStickyBar found in server response');
      return;
    }

    // Store current visibility state before morphing
    const currentStuck = this.refs.stickyBar.getAttribute('data-stuck') || 'false';
    const variantAvailable = newStickyAddToCart.dataset.variantAvailable;
    const variantSoldOut = newStickyAddToCart.dataset.variantSoldOut;

    // Morph the entire sticky bar content
    morph(this.refs.stickyBar, newStickyBar, { childrenOnly: true });

    // Restore visibility state after morphing
    this.refs.stickyBar.setAttribute('data-stuck', currentStuck);
    this.dataset.variantAvailable = variantAvailable;
    if (variantSoldOut !== undefined) {
      this.dataset.variantSoldOut = variantSoldOut;
    }

    // Update the dataset attributes with new variant info
    if (variant && variant.id) {
      this.dataset.currentVariantId = variant.id;
    }

    // Re-cache the target add to cart button after morphing
    const productForm = this.#getProductForm();
    if (productForm) {
      this.#targetAddToCartButton = productForm.querySelector('[ref="addToCartButton"]');
    }

    // Update sticky bar button state and text based on variant availability
    // Use setTimeout to ensure morph has completed and DOM is updated
    setTimeout(() => {
      // Re-query the button after morphing to ensure we have the latest reference
      const stickyAddToCartComponent = /** @type {HTMLElement & { enable?: () => void, disable?: () => void } | null} */ (
        this.querySelector('add-to-cart-component')
      );

      const currentButton = this.querySelector('add-to-cart-component button[ref="addToCartButton"]');

      if (stickyAddToCartComponent && currentButton) {
        // Update button state: enable if variant is available, disable if sold out or unavailable
        // Use variantAvailable from dataset as it's more reliable (comes from server)
        if (variantAvailable === 'true') {
          stickyAddToCartComponent.enable?.();
        } else {
          stickyAddToCartComponent.disable?.();
        }

        // Update button text - always determine from variant state to ensure correctness
        const currentButtonTextContent = currentButton.querySelector('.add-to-cart-text__content span span');
        if (currentButtonTextContent) {
          let buttonText = '';

          if (variantAvailable === 'true') {
            buttonText = '+ Add to Cart';
          } else if (variantSoldOut === 'true') {
            buttonText = 'Sold out';
          } else {
            buttonText = 'Unavailable';
          }

          if (buttonText) {
            currentButtonTextContent.textContent = buttonText;
          }
        }

        // Update icon visibility: show icon for available variants, hide otherwise
        const currentButtonIcon = currentButton.querySelector('.add-to-cart-icon');
        if (currentButtonIcon) {
          currentButtonIcon.style.display = variantAvailable === 'true' ? '' : 'none';
        }
      }
    }, 0);

    if (variant == null) {
      this.#handleVariantUnavailable();
    }

    // Re-initialize quantity syncing after morphing so sticky controls stay wired
    this.#setupQuantitySync();

    // Ensure the sticky quantity input reflects the current quantity and does not reset to 1
    if (this.#stickyQuantitySelector) {
      const stickyQuantityInput = /** @type {HTMLInputElement | null} */ (
        this.#stickyQuantitySelector.querySelector('input[ref="quantityInput"]')
      );

      if (stickyQuantityInput) {
        stickyQuantityInput.value = String(this.#currentQuantity);
      }
    }

    // Restore the current quantity display if needed
    this.#updateButtonText();
  };

  /**
   * Handles variant selected events
   * @param {CustomEvent} event - The variant selected event
   */
  #handleVariantSelected = (event) => {
    // The variant update event will follow and handle all updates via morph
    // We just update the dataset here for tracking
    const variantId = event.detail.resource?.id;
    if (!variantId) return;
    this.dataset.currentVariantId = variantId;
  };

  /**
   * Updates the variant title based on selected options when the variant is unavailable
   */
  #handleVariantUnavailable = () => {
    this.dataset.currentVariantId = '';
    const variantTitleElement = this.querySelector('.sticky-add-to-cart__variant');
    const productId = this.dataset.productId;
    const variantPicker = document.querySelector(`variant-picker[data-product-id="${productId}"]`);
    if (!variantTitleElement || !variantPicker) return;

    const selectedOptions = Array.from(variantPicker.querySelectorAll('input:checked'))
      .map((option) => /** @type {HTMLInputElement} */(option).value)
      .filter((value) => value !== '')
      .join(' / ');
    if (!selectedOptions) return;
    variantTitleElement.textContent = selectedOptions;
  };

  /**
   * Handles cart add complete (success or error)
   * Resets puppet flag and, on mobile sticky usage, can surface max-quantity messaging.
   * @param {CustomEvent} event - The cart event
   */
  #handleCartAddComplete = (event) => {
    // Reset the puppet flag after cart operation
    if (this.#targetAddToCartButton) {
      this.#targetAddToCartButton.dataset.puppet = 'false';
    }

    // Only show extra messaging for mobile sticky usage
    const isMobile =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(max-width: 767px)').matches;

    // Only proceed if this add came from the product form and relates to this product
    const detailData = event?.detail?.data;
    if (!isMobile || !detailData || detailData.source !== 'product-form-component') return;
    if (detailData.productId !== this.dataset.productId) return;

    // If there was no add error, do nothing special
    if (!detailData.didError) return;

    const section = this.closest('.shopify-section');
    if (!section) return;

    /** @type {HTMLElement | null} */
    const variantPicker = section.querySelector(
      'variant-picker:not(product-card variant-picker):not(quick-add-dialog variant-picker)'
    );

    /** @type {HTMLElement | null} */
    const variantError =
      (variantPicker && variantPicker.querySelector('[data-variant-error]')) ||
      section.querySelector('[data-variant-error]');

    if (!variantError) return;

    // For now, show a generic "max quantity reached" message for sticky mobile flows
    variantError.textContent = 'The maximum quantity of this item is already in your cart.';
    variantError.classList.remove('hidden');

    if (variantPicker) {
      variantPicker.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  };

  /**
   * Handles quantity selector update events
   * @param {QuantitySelectorUpdateEvent} event - The quantity update event
   */
  #handleQuantityUpdate = (event) => {
    // Only respond to product page quantity selector updates, not cart drawer
    if (event.detail.cartLine) return;

    this.#currentQuantity = event.detail.quantity;

    // Keep the sticky quantity input visually in sync with the main selector
    if (this.#stickyQuantitySelector) {
      const quantityInput = /** @type {HTMLInputElement | null} */ (
        this.#stickyQuantitySelector.querySelector('input[ref="quantityInput"]')
      );
      if (quantityInput) {
        quantityInput.value = String(this.#currentQuantity);
      }
    }

    this.#updateButtonText();
  };

  /**
   * Shows the sticky bar with animation
   */
  #showStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = true;
    stickyBar.dataset.stuck = 'true';
  }

  /**
   * Hides the sticky bar with animation
   */
  #hideStickyBar() {
    const { stickyBar } = this.refs;
    this.#isStuck = false;
    stickyBar.dataset.stuck = 'false';
  }

  // Helper methods
  /**
   * Gets the product form element
   * @returns {HTMLElement | null}
   */
  #getProductForm() {
    const productId = this.dataset.productId;
    if (!productId) return null;

    const sectionElement = this.closest('.shopify-section');
    if (!sectionElement) return null;

    const sectionId = sectionElement.id.replace('shopify-section-', '');
    return document.querySelector(
      `#shopify-section-${sectionId} product-form-component[data-product-id="${productId}"]`
    );
  }

  /**
   * Gets the initial quantity from the data attribute
   */
  #getInitialQuantity() {
    this.#currentQuantity = parseInt(this.dataset.initialQuantity || '1') || 1;
    this.#updateButtonText();
  }

  /**
   * Updates the button text to include quantity
   */
  #updateButtonText() {
    const { addToCartButton, quantityDisplay, quantityNumber } = this.refs;
    if (!addToCartButton || !quantityDisplay || !quantityNumber) return;

    const available = !addToCartButton.disabled;

    // Update the quantity number
    quantityNumber.textContent = this.#currentQuantity.toString();

    // Show/hide the quantity display based on availability and quantity
    if (available && this.#currentQuantity > 1) {
      quantityDisplay.style.display = 'inline';
    } else {
      quantityDisplay.style.display = 'none';
    }
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCartComponent);
}

